import NotificationEvents from '@Core/components/notificationBanner/events';
import Component from '@bythepixel/component-loader';
import { eventThrottle } from '@Utility';
import LeadForm from '../leadForm';
import LocationSearchField from '../locationSearchField';

export default class DigitalCallTree extends Component {
  static selector = '[data-comp-digital-call-tree]';
  static closeSelector = '[data-comp-close-trigger]';
  static backSelector = '[data-comp-back-trigger]';
  static geolocator = '[data-comp-dct-geolocator]';
  static contentComponentSelector = '.slide-out-modal__content';

  loadingComponent = null;

  contentComponent = null;

  formEl = null;

  constructor(containerEl, options) {
    super(containerEl, options);

    this.contentComponent = this.$container.querySelector(
      DigitalCallTree.contentComponentSelector,
    );

    this.registerCloseButtonEvents();

    this.registerBackButtonEvents();

    this.registerClickOutsideEvent();

    this.initLivewireListeners();
  }

  geolocate() {
    if (!navigator.geolocation) {
      this.geolocateError('Geolocation is not supported by your browser.');

      return;
    }

    this.updateButtonStyling(true);

    const errorText = 'We were unable to find your location. Please turn on location services to use your location.';

    navigator.geolocation.getCurrentPosition(
      (position) => this.geolocateSuccess(position),
      () => this.geolocateError(errorText),
    );
  }

  geolocateSuccess(position) {
    const { latitude, longitude } = position.coords;
    window.Livewire?.dispatch('dct:geolocated', { latitude, longitude });
    this.updateButtonStyling(false);
  }

  geolocateError(message) {
    window.dispatchEvent(NotificationEvents.Notify(message));
    this.updateButtonStyling(false);
  }

  registerCloseButtonEvents() {
    const closeButton = this.$container.querySelector(
      DigitalCallTree.closeSelector,
    );

    closeButton?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.closeDialog();
    });
  }

  registerBackButtonEvents() {
    const backButton = this.$container.querySelector(
      DigitalCallTree.backSelector,
    );

    // start hidden, let page logic dictate when to toggle
    backButton?.classList.add('hidden');

    backButton?.addEventListener('click', (e) => {
      e.stopPropagation();
      window.Livewire?.dispatch('dct:back');
    });
  }

  /**
   * Closes dialog when clicking on backdrop
   */
  registerClickOutsideEvent() {
    this.$container.addEventListener('click', (event) => {
      event.stopPropagation();

      if (event.target.nodeName !== 'DIALOG') {
        return;
      }

      const rect = this.$container.getBoundingClientRect();
      const isInDialog = rect.top <= event.clientY
        && event.clientY <= rect.top + rect.height
        && rect.left <= event.clientX
        && event.clientX <= rect.left + rect.width;

      if (!isInDialog) {
        this.closeDialog();
      }
    });
  }

  closeDialog() {
    this.$container.close('dismiss');
    window.Livewire?.dispatch('dct:dialog-closed');
  }

  initLivewireListeners() {
    window.Livewire?.on('dct:close-dialog', () => {
      this.$container.close('dismiss');
    });

    window.Livewire?.on('dct:page-changed', (e) => {
      const canGoBack = e.pageHistory.length > 1;

      const backButton = this.$container.querySelector(
        DigitalCallTree.backSelector,
      );

      backButton?.classList.toggle('hidden', !canGoBack);
    });

    window.Livewire?.on('dct:init-location-search', () => {
      const geolocatorButton = this.$container.querySelector(
        DigitalCallTree.geolocator,
      );

      geolocatorButton?.addEventListener('click', () => {
        this.geolocate();
      });

      const searchField = this.$container.querySelector(
        LocationSearchField.selector,
      );
      if (!searchField) return;
      // eslint-disable-next-line no-new
      new LocationSearchField(searchField);
    });

    window.Livewire?.on('dct:init-contact-form', () => {
      const form = this.$container.querySelector(LeadForm.selector);
      if (!form || form?.isSameNode(this.formEl)) return;
      this.formEl = form;
      // eslint-disable-next-line no-new
      new LeadForm(form);
    });

    // This section is what prevents livewire from flashing the current page right before navigating to next page
    window.Livewire?.on('dct:loading', ([componentName]) => {
      this.loadingComponent = componentName;
      this.contentComponent?.firstElementChild.classList.add('dct--is-loading');
      this.contentComponent?.lastElementChild.classList.add('loader--fade-in');
    });

    window.Livewire?.on('dct:loaded', ([componentName]) => {
      if (this.loadingComponent === componentName) return;
      this.loadingComponent = null;
      this.contentComponent?.firstElementChild.classList.remove(
        'dct--is-loading',
      );
      this.contentComponent?.lastElementChild.classList.remove(
        'loader--fade-in',
      );
    });

    const swapNumbers = () => {
      setTimeout(() => {
        window.CallTrk?.swap();
      }, 250);
    };

    window.Livewire?.hook(
      'morph.added',
      eventThrottle(() => swapNumbers(), 250),
    );
  }

  updateButtonStyling(loading = true) {
    const geolocatorButton = this.$container.querySelector(
      DigitalCallTree.geolocator,
    );

    if (!geolocatorButton) return;

    geolocatorButton.classList.toggle('is-loading', loading);
  }
}
